import {
  type Chain,
  type DynamicClient,
  type WalletProviderMetadata,
  assertWalletAccountSigningAvailability,
} from '@dynamic-labs-sdk/client';
import {
  DYNAMIC_ICONIC_SPRITE_URL,
  assertDefined,
  createWalletProviderEventEmitter,
  formatWalletProviderGroupKey,
  formatWalletProviderKey,
} from '@dynamic-labs-sdk/client/core';
import { WalletProviderEnum } from '@dynamic-labs/sdk-api-core';
import { Psbt } from 'bitcoinjs-lib';

import type {
  BitcoinSendTransactionResponse,
  BitcoinWalletProvider,
} from '../../../../BitcoinWalletProvider.types';
import { bitcoinExecuteCheckoutTransaction } from '../../../../utils/bitcoinExecuteCheckoutTransaction';
import { bitcoinTransferAmount } from '../../../../utils/bitcoinTransferAmount';
import { getNetworkIdFromNetworkName } from '../../../../utils/getNetworkIdFromNetworkName';
import type { OneKeyBitcoinInjectedProvider } from '../OneKeyBitcoinInjectedProvider.types';

export const ONEKEY_METADATA: WalletProviderMetadata = {
  displayName: 'OneKey',
  icon: `${DYNAMIC_ICONIC_SPRITE_URL}#onekey`,
};

type CreateOneKeyBitcoinWalletProviderParams = {
  dynamicClient: DynamicClient;
  injectedProvider: OneKeyBitcoinInjectedProvider;
};

export const createOneKeyBitcoinWalletProvider = ({
  dynamicClient,
  injectedProvider,
}: CreateOneKeyBitcoinWalletProviderParams): BitcoinWalletProvider => {
  const chain: Chain = 'BTC';

  const walletProviderType = WalletProviderEnum.BrowserExtension;

  const key = formatWalletProviderKey({
    chain,
    displayName: ONEKEY_METADATA.displayName,
    walletProviderType,
  });

  const connect: BitcoinWalletProvider['connect'] = async () => {
    const accounts = await injectedProvider.requestAccounts();

    const address = accounts[0];
    const publicKey = await injectedProvider.getPublicKey();

    return {
      addresses: [{ address, publicKey }],
    };
  };

  const getActiveNetworkId: BitcoinWalletProvider['getActiveNetworkId'] =
    async () => {
      const network = await injectedProvider.getNetwork();

      return getNetworkIdFromNetworkName(
        { networkName: network },
        dynamicClient
      );
    };

  const getConnectedAddresses: BitcoinWalletProvider['getConnectedAddresses'] =
    async () => {
      const accounts = await injectedProvider.getAccounts();

      return {
        addresses: accounts,
      };
    };

  const sendBitcoin: BitcoinWalletProvider['sendBitcoin'] = async ({
    transaction,
    walletAccount,
  }): Promise<BitcoinSendTransactionResponse> => {
    await assertWalletAccountSigningAvailability(
      { walletAccount },
      dynamicClient
    );

    const transactionId = await injectedProvider.sendBitcoin(
      transaction.recipientAddress,
      transaction.amount.toString()
    );

    return {
      transactionId,
    };
  };

  const signMessage: BitcoinWalletProvider['signMessage'] = async ({
    message,
    walletAccount,
    protocol,
  }) => {
    assertDefined(walletAccount, 'Wallet account not found');

    await assertWalletAccountSigningAvailability(
      { walletAccount },
      dynamicClient
    );

    const signedMessage = await injectedProvider.signMessage(message, protocol);

    return {
      signature: signedMessage,
    };
  };

  const signPsbt: BitcoinWalletProvider['signPsbt'] = async ({
    request,
    walletAccount,
  }) => {
    await assertWalletAccountSigningAvailability(
      { walletAccount },
      dynamicClient
    );

    const psbtFromBase64 = Psbt.fromBase64(request.unsignedPsbtBase64);

    const signedPsbtHex = await injectedProvider.signPsbt(
      psbtFromBase64.toHex(),
      { autoFinalized: false }
    );

    return { signedPsbt: Psbt.fromHex(signedPsbtHex).toBase64() };
  };

  const signPsbts: BitcoinWalletProvider['signPsbts'] = async ({
    requests,
    walletAccount,
  }) => {
    await assertWalletAccountSigningAvailability(
      { walletAccount },
      dynamicClient
    );

    const psbtHexs: string[] = [];

    for (const request of requests) {
      const psbtFromBase64 = Psbt.fromBase64(request.unsignedPsbtBase64);

      psbtHexs.push(psbtFromBase64.toHex());
    }

    const signedPsbtHexs = await injectedProvider.signPsbts(psbtHexs, {
      autoFinalized: false,
    });

    return {
      signedPsbts: signedPsbtHexs.map((signedPsbtHex) =>
        Psbt.fromHex(signedPsbtHex).toBase64()
      ),
    };
  };

  const { getEventEmitter, cleanupEventEmitter } =
    createWalletProviderEventEmitter({
      removeEventListeners: () => {
        injectedProvider?.removeAllListeners();
      },

      setupEventListeners: ({
        handleAccountsChanged,
        handleNetworkChanged,
      }) => {
        injectedProvider?.on('accountsChanged', (addresses) => {
          handleAccountsChanged({ addresses });
        });

        injectedProvider?.on('networkChanged', (network) => {
          handleNetworkChanged({ networkId: network });
        });
      },

      supportedEvents: ['accountsChanged', 'networkChanged'],
    });

  const terminate: BitcoinWalletProvider['terminate'] = async () => {
    cleanupEventEmitter();
  };

  return {
    chain,
    connect,
    get events() {
      return getEventEmitter();
    },
    executeCheckoutTransaction: (args) =>
      bitcoinExecuteCheckoutTransaction(args, dynamicClient),
    getActiveNetworkId,
    getConnectedAddresses,
    groupKey: formatWalletProviderGroupKey(ONEKEY_METADATA.displayName),
    key,
    metadata: ONEKEY_METADATA,
    sendBitcoin,
    signMessage,
    signPsbt,
    signPsbts,
    terminate,
    transferAmount: (args) => bitcoinTransferAmount(args, dynamicClient),
    walletProviderType,
  };
};
